import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { getProspectSearchBootstrap } from "@/app/actions/prospect-search/bootstrap/action";
import { seedProspectSearchWorkspace } from "@/app/hooks/fetchs/prospect-search/useFetchProspectSearchWorkspace";
import { ProspectSearchBootstrap } from "@/app/types/prospect/ProspectSearchBootstrap";
import { UCookie } from "@/app/utils/UCookie";
import { UProspectFilter } from "@/app/utils/UProspectFilter";
import { QueryKey } from "@/app/vars/QueryKeys";
import { CookieKey } from "@/app/vars/Storage";

type Options = {
  projectId?: number;
  enabled?: boolean;
  initialData?: ProspectSearchBootstrap;
};

export function useFetchProspectSearchBootstrap(options: Options) {
  const { projectId, enabled = true, initialData } = options;
  const queryClient = useQueryClient();

  const seedCaches = (bootstrap: ProspectSearchBootstrap) => {
    queryClient.setQueryData([QueryKey.PROJECT_OFFER, projectId], bootstrap.offers);
    queryClient.setQueryData([QueryKey.PROSPECT_ACTION_TYPES, projectId], bootstrap.actionTypes);

    if (bootstrap.selectedOfferId) {
      queryClient.setQueryData(
        [QueryKey.CLIENT_AVATAR, bootstrap.selectedOfferId],
        bootstrap.avatars,
      );
    }

    const avatarId = bootstrap.selectedAvatarId;
    const workspace = bootstrap.workspace;
    if (avatarId && workspace) {
      queryClient.setQueryData([QueryKey.PROSPECT_SEARCH_WORKSPACE, avatarId], workspace);
      seedProspectSearchWorkspace(queryClient, avatarId, workspace);
    }
  };

  const seededProjectId = useRef<number | undefined>(undefined);
  if (initialData && projectId && seededProjectId.current !== projectId) {
    seedCaches(initialData);
    seededProjectId.current = projectId;
  }

  return useQuery({
    queryKey: [QueryKey.PROSPECT_SEARCH_BOOTSTRAP, projectId],
    queryFn: async () => {
      const hintOfferId = UCookie.readNumber(CookieKey.SELECTED_OFFER);
      const hintAvatarId = UCookie.readNumber(CookieKey.SELECTED_AVATAR);

      const bootstrap = await getProspectSearchBootstrap(
        projectId!,
        hintOfferId,
        hintAvatarId,
        UProspectFilter.readStoredTab(),
        UProspectFilter.readStoredActionFilter(),
      );
      seedCaches(bootstrap);

      return bootstrap;
    },
    enabled: enabled && !!projectId,
    staleTime: Infinity,
    initialData: seededProjectId.current === projectId ? initialData : undefined,
  });
}
